import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, RevealLines } from "./motion";
import { IMG } from "../data/site";

export const SectionLabel = ({ children, dark = false, className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <span className={`h-px w-8 ${dark ? "bg-brand-300" : "bg-brand-800"}`} />
    <span className={`text-xs font-semibold uppercase tracking-[0.2em] ${dark ? "text-brand-200" : "text-brand-800"}`}>
      {children}
    </span>
  </div>
);

export const BtnPrimary = ({ to, children, testid, className = "" }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`group inline-flex items-center justify-center gap-2 bg-brand-800 text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-brand-900 transition-colors active:scale-[0.98] ${className}`}
  >
    {children}
    <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </Link>
);

export const BtnGhost = ({ to, children, testid, dark = false, className = "" }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`group inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-semibold uppercase tracking-wider border transition-colors active:scale-[0.98] ${
      dark
        ? "border-white/40 text-white hover:bg-white hover:text-brand-900"
        : "border-brand-800 text-brand-800 hover:bg-brand-50"
    } ${className}`}
  >
    {children}
    <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </Link>
);

// Inner-page hero band.
export const PageHero = ({ label, titleLines, description }) => (
  <section className="relative pt-32 md:pt-44 pb-16 md:pb-24 bg-brand-900 text-white overflow-hidden">
    <div className="absolute inset-0 bp-grid-dark opacity-60" />
    <div className="absolute -right-40 -top-40 w-[520px] h-[520px] rounded-full bg-brand-700/30 blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      <SectionLabel dark>{label}</SectionLabel>
      <h1 className="mt-6 font-heading text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95]">
        <RevealLines lines={titleLines} />
      </h1>
      {description && (
        <Reveal delay={0.3} className="mt-8 max-w-2xl text-brand-100 text-base md:text-lg leading-relaxed">
          {description}
        </Reveal>
      )}
    </div>
  </section>
);

// Reusable closing contact CTA.
export const CtaSection = () => (
  <section className="relative overflow-hidden bg-brand-900 text-white">
    <div
      className="absolute inset-0 opacity-25 bg-cover bg-center"
      style={{ backgroundImage: `url(${IMG.ctaTexture})` }}
      aria-hidden="true"
    />
    <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36 text-center flex flex-col items-center">
      <SectionLabel dark className="justify-center">Get in touch</SectionLabel>
      <h2 className="mt-6 font-heading text-4xl md:text-6xl font-bold tracking-tighter max-w-3xl text-balance">
        Let's Build a Cleaner Future Together
      </h2>
      <p className="mt-6 max-w-xl text-brand-100 text-base md:text-lg">
        Talk to our engineering team about your facility's medical waste treatment and sterilization requirements.
      </p>
      <div className="mt-10">
        <Link
          to="/contact"
          data-testid="cta-contact-btn"
          className="group inline-flex items-center gap-2 bg-white text-brand-900 px-10 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-brand-100 transition-colors active:scale-[0.98]"
        >
          Contact STERILTRON
          <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </div>
    </div>
  </section>
);
